import { Controller } from "@hotwired/stimulus"
import { Offcanvas } from "bootstrap"

// Panneau des réactions de la frise détaillée (Insurance::ReactionRuns::PanelComponent) : une ligne de réaction charge
// son exécution dans la frame du panneau, puis l'ouvre. Le panneau est sorti sous body : la modale scrollable de la
// frise ne laisserait pas cliquer sa croix. Sa poignée gauche l'étire ou le resserre comme la sidebar
// (sidebar_resize_controller) ; poussé sous sa largeur minimale, il se ferme. Largeur gardée dans ce navigateur.
// Un clic hors du panneau (dans la modale) le ferme, sauf sur une autre ligne de réaction, qui s'y charge.
const WIDTH_DEFAULT = 448 // 28rem
const WIDTH_MIN = 320
const CLOSE_BELOW = 240
const KEYBOARD_STEP = 16
const STORAGE_KEY = "depozen:reaction-run-panel-width"

export default class extends Controller {
  static targets = ["panel", "frame", "handle"]

  connect() {
    this.panel = this.panelTarget
    this.handle = this.hasHandleTarget ? this.handleTarget : null
    const stored = this.readStoredWidth()
    if (stored) this.applyWidth(stored)

    // La frise est dans une modale scrollable : un offcanvas fixed qui en dépasse n'y reçoit pas les clics
    // (la croix, à droite). On le sort sous body, au-dessus de la modale, et on ferme avec la même instance
    // que open() — data-bs-dismiss en ouvrirait une autre, qui se croit fermée.
    this.modal = this.element.closest(".modal")
    this.onModalHidden = () => Offcanvas.getInstance(this.panel)?.hide()
    this.modal?.addEventListener("hidden.bs.modal", this.onModalHidden)
    this.panel.addEventListener("click", this.close)
    document.addEventListener("click", this.closeOnOutsideClick)
    // Le piège à focus de la modale (focusin sur document) ramène dans la modale tout focus qui en sort : un clic dans
    // le texte du panneau (tabindex -1) y renvoyait le focus et effaçait la sélection. Le focus du panneau s'arrête à lui.
    this.panel.addEventListener("focusin", this.isolateFocus)
    // « Effet » ou « Réaction » : l'en-tête suit ce que la frame montre, y compris après un lien du panneau (un effet
    // mène à la réaction qui prend sa suite).
    this.panel.addEventListener("turbo:frame-load", this.syncTitle)
    // La poignée part avec le panneau, hors du contrôleur : ses actions Stimulus ne suivent pas.
    this.handle?.addEventListener("pointerdown", (event) => this.startResize(event))
    this.handle?.addEventListener("dblclick", (event) => this.resetWidth(event))
    this.handle?.addEventListener("keydown", (event) => this.resizeKeydown(event))
    document.body.appendChild(this.panel)
  }

  disconnect() {
    this.stopResize()
    this.modal?.removeEventListener("hidden.bs.modal", this.onModalHidden)
    document.removeEventListener("click", this.closeOnOutsideClick)
    this.panel?.remove()
  }

  open(event) {
    event.preventDefault()
    const url = new URL(event.currentTarget.dataset.url, window.location.href).href
    this.setTitle(event.currentTarget.dataset.panelTitle)
    const frame = this.frameElement()
    this.showSkeleton(frame)

    if (frame.src === url) {
      frame.reload()
    } else {
      frame.src = url
    }
    Offcanvas.getOrCreateInstance(this.panel).show()
  }

  close = (event) => {
    if (!event.target.closest("[data-reaction-run-panel-close]")) return

    event.preventDefault()
    event.stopPropagation()
    Offcanvas.getOrCreateInstance(this.panel).hide()
  }

  // Le panneau n'a pas de fond (data-bs-backdrop="false") : Bootstrap ne le ferme pas au clic extérieur.
  closeOnOutsideClick = (event) => {
    if (!this.panel.classList.contains("show") || this.resizeJustEnded) return
    if (this.panel.contains(event.target)) return
    if (event.target.closest("[data-action~='reaction-run-panel#open']")) return

    Offcanvas.getInstance(this.panel)?.hide()
  }

  syncTitle = () => {
    this.setTitle(this.frameElement()?.querySelector("[data-reaction-run-panel-title]")?.dataset.reactionRunPanelTitle)
  }

  setTitle(title) {
    const header = this.panel.querySelector("[data-reaction-run-panel-header-title]")
    if (header && title) header.textContent = title
  }

  isolateFocus = (event) => {
    event.stopPropagation()
  }

  frameElement() {
    return this.panel.querySelector("[data-reaction-run-panel-target='frame']")
  }

  // La frame garde la réaction d'avant jusqu'à la réponse : le panneau l'aurait montrée le temps du chargement.
  showSkeleton(frame) {
    const template = this.panel.querySelector("template[data-reaction-run-panel-skeleton]")
    if (template) frame.replaceChildren(template.content.cloneNode(true))
  }

  startResize(event) {
    event.preventDefault()
    this.resizing = true
    this.startX = event.clientX
    this.startWidth = this.panel.getBoundingClientRect().width
    this.panel.classList.add("is-resizing")
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    document.addEventListener("pointermove", this.moveResize)
    document.addEventListener("pointerup", this.stopResize)
    document.addEventListener("pointercancel", this.stopResize)
  }

  // Ancré à droite : tirer vers la gauche élargit.
  moveResize = (event) => {
    if (!this.resizing) return

    const width = this.startWidth + (this.startX - event.clientX)
    // Fermé d'un geste : il rouvrira à la largeur d'avant le geste.
    if (width < CLOSE_BELOW) {
      this.applyWidth(this.startWidth)
      this.stopResize()
      Offcanvas.getOrCreateInstance(this.panel).hide()
      return
    }
    this.applyWidth(this.clamp(width))
  }

  stopResize = () => {
    if (!this.resizing) return

    this.resizing = false
    // Relâché hors du panneau, le geste se termine par un clic sur la modale : ce n'est pas un clic extérieur.
    this.resizeJustEnded = true
    setTimeout(() => { this.resizeJustEnded = false })
    this.panel.classList.remove("is-resizing")
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    document.removeEventListener("pointermove", this.moveResize)
    document.removeEventListener("pointerup", this.stopResize)
    document.removeEventListener("pointercancel", this.stopResize)
    this.storeWidth(this.panel.getBoundingClientRect().width)
  }

  resetWidth(event) {
    event.preventDefault()
    this.applyWidth(WIDTH_DEFAULT)
    this.storeWidth(null)
  }

  resizeKeydown(event) {
    const width = this.panel.getBoundingClientRect().width
    const next = { ArrowLeft: width + KEYBOARD_STEP, ArrowRight: width - KEYBOARD_STEP, Home: WIDTH_MIN, End: this.maxWidth() }[event.key]
    if (next === undefined) return

    event.preventDefault()
    this.applyWidth(this.clamp(next))
    this.storeWidth(this.panel.getBoundingClientRect().width)
  }

  maxWidth() {
    return Math.max(WIDTH_MIN, Math.min(window.innerWidth * 0.9, 1024))
  }

  clamp(width) {
    return Math.round(Math.min(Math.max(width, WIDTH_MIN), this.maxWidth()))
  }

  applyWidth(width) {
    this.panel.style.setProperty("--bs-offcanvas-width", `${this.clamp(width)}px`)
    if (this.handle) this.handle.setAttribute("aria-valuenow", String(this.clamp(width)))
  }

  readStoredWidth() {
    try {
      const value = Number(window.localStorage.getItem(STORAGE_KEY))
      return value > 0 ? value : null
    } catch {
      return null
    }
  }

  storeWidth(width) {
    try {
      if (width) {
        window.localStorage.setItem(STORAGE_KEY, String(Math.round(width)))
      } else {
        window.localStorage.removeItem(STORAGE_KEY)
      }
    } catch {
      // Stockage indisponible (navigation privée) : la largeur vaut pour cette page seulement.
    }
  }
}
